import React from 'react';
import {
  Alert,
  Area,
  Button,
  IconButton,
  Icons,
  Modal,
  Tooltip,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { COMMON_ROOM_INFO_MESSAGE } from './constants';
import { createStyles } from './CommonRoomInfoAlert.styles';

interface CommonRoomInfoButtonProps {
  isDesktop: boolean;
  onClick: () => void;
}

export const CommonRoomInfoButton = ({
  isDesktop,
  onClick,
}: CommonRoomInfoButtonProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const button = (
    <IconButton
      aria-label={COMMON_ROOM_INFO_MESSAGE}
      onClick={onClick}
      sx={styles.infoButton}
    >
      <Tooltip
        disableInteractive
        disableFocusListener={!isDesktop}
        disableHoverListener={!isDesktop}
        disableTouchListener={!isDesktop}
        position="top"
        title={COMMON_ROOM_INFO_MESSAGE}
      >
        <Icons.InfoOutlined aria-hidden="true" />
      </Tooltip>
    </IconButton>
  );

  return button;
};

interface CommonRoomInfoAlertProps {
  visible: boolean;
  isDesktop: boolean;
  onClose: () => void;
}

const CommonRoomInfoAlert = ({
  visible,
  isDesktop,
  onClose,
}: CommonRoomInfoAlertProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);

  if (!visible) return null;

  return (
    <Alert
      severity="info"
      icon={<Icons.InfoOutlined sx={styles.alertIcon} />}
      sx={styles.alert}
    >
      <Typography variant="body2" sx={styles.alertMessage}>
        {COMMON_ROOM_INFO_MESSAGE}
      </Typography>

      <Tooltip
        position="top"
        title="Close information"
        disableFocusListener={!isDesktop}
        disableHoverListener={!isDesktop}
        disableTouchListener={!isDesktop}
      >
        <IconButton
          aria-label="Close Common Room information"
          onClick={onClose}
          size="small"
          sx={styles.alertCloseButton}
        >
          <Icons.Close fontSize="small" />
        </IconButton>
      </Tooltip>
    </Alert>
  );
};

interface CommonRoomInfoModalProps {
  open: boolean;
  onClose: () => void;
}

export const CommonRoomInfoModal = ({
  open,
  onClose,
}: CommonRoomInfoModalProps) => {
  const styles = createStyles(useTheme());

  return (
    <Modal open={open} onClose={onClose}>
      <Area
        rounded
        elevated
        role="dialog"
        aria-modal="true"
        aria-labelledby="common-room-information-title"
        sx={styles.modalContainer}
      >
        <Area sx={styles.modalHeader}>
          <Typography
            id="common-room-information-title"
            component="h2"
            variant="h6"
          >
            Information
          </Typography>

          <IconButton
            aria-label="Close Common Room information dialog"
            onClick={onClose}
          >
            <Icons.Close />
          </IconButton>
        </Area>

        <Area sx={styles.modalContent}>
          <Typography>{COMMON_ROOM_INFO_MESSAGE}</Typography>
        </Area>

        <Area sx={styles.modalFooter}>
          <Button aria-label="Close dialog" onClick={onClose}>
            Close
          </Button>
        </Area>
      </Area>
    </Modal>
  );
};

export default CommonRoomInfoAlert;
